import React, { ReactNode, useEffect, useRef } from 'react';

/**
 * Layout props interface
 *
 * AIDEV-NOTE: Declarative Layout Composition (2025 Best Practices)
 * =================================================================
 * Routes declare their layout needs via props - NO useEffect needed.
 *
 * PATTERN:
 * - Default: Layout renders header/footer slots based on props
 * - Custom header: Pass customHeader prop to render specific header
 * - Custom footer: Pass customFooter prop to render specific footer
 * - gradientType: Configure header dissolution gradient intensity
 *
 * ARCHITECTURE (Refactored):
 * - Header and BottomNavigation are now pure presentational components
 * - Auth logic is handled by GuestLayout or StandardLayout
 * - Layout just provides the structural frame - no default header/footer
 *
 * USAGE:
 * <Layout customHeader={<Header {...props} />} customFooter={<BottomNavigation {...props} />}>
 *   <Content />
 * </Layout>
 *
 * AIDEV-NOTE: Gradient System for Header Dissolution
 * ===================================================
 * gradientType options:
 * - 'none': No gradient overlay (clean separation)
 * - 'subtle': 40px fade (minimal visual effect)
 * - 'standard': 60px multi-layer fade (balanced)
 * - 'prominent': 100px multi-layer fade (strong dissolution effect)
 *
 * Custom gradient via gradientHeight and gradientClassName for special cases
 */
interface LayoutProps {
  children: ReactNode;
  customHeader?: ReactNode;
  customFooter?: ReactNode;
  showDefaultHeader?: boolean;
  showDefaultFooter?: boolean;
  gradientType?: 'none' | 'subtle' | 'standard' | 'prominent';
  gradientHeight?: string; // Custom Tailwind height class (e.g., 'h-24')
  gradientClassName?: string; // Full custom gradient class override
  mainClassName?: string; // Custom className for main element
  mainStyle?: React.CSSProperties; // Custom inline styles for main element
  footerStyle?: React.CSSProperties; // Custom inline styles for footer wrapper (default: bottom nav offset)
  enableScrollSentinel?: boolean; // Enable sentinel for scroll detection
  onScrollSentinelChange?: (isAtTop: boolean) => void; // Callback when scroll position changes
}

/**
 * Main layout component that provides consistent structure across all screens
 *
 * AIDEV-NOTE: Scroll Architecture - Route-Based Pattern
 * ======================================================
 * This Layout is a flex child of #app and provides the frame for all routes.
 * Each route decides its own scroll behavior:
 *
 * - Feed/List screens (Messages): Natural page scroll with overscroll-y-auto
 * - Feed screens with pull-to-refresh (Home): Natural page scroll
 * - Fixed screens (Chat): Route uses h-full + overflow-hidden
 * - Detail screens (QuestionDetails, Profiles): Natural scroll
 *
 * Layout does NOT impose scroll restrictions - routes are autonomous.
 */
export const Layout: React.FC<LayoutProps> = ({
  children,
  customHeader,
  customFooter,
  showDefaultHeader = true,
  showDefaultFooter = true,
  gradientType = 'standard',
  gradientHeight,
  gradientClassName,
  mainClassName,
  mainStyle,
  footerStyle,
  enableScrollSentinel = false,
  onScrollSentinelChange,
}) => {
  // AIDEV-NOTE: Header/Footer now come from parent layouts (GuestLayout, StandardLayout)
  // showDefaultHeader/showDefaultFooter are kept for backwards compatibility but
  // primary pattern is to pass customHeader/customFooter from parent layouts
  const hasHeader = customHeader || showDefaultHeader;
  const hasFooter = customFooter || showDefaultFooter;
  const sentinelRef = useRef<HTMLDivElement>(null);

  // AIDEV-NOTE: IntersectionObserver for scroll detection (sentinel pattern)
  // Runs off main thread, more performant than scroll listeners
  useEffect(() => {
    if (!enableScrollSentinel || !sentinelRef.current || !onScrollSentinelChange) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // When sentinel is visible = at top, not visible = scrolled down
        onScrollSentinelChange(entry.isIntersecting);
      },
      {
        threshold: 0,
        rootMargin: '0px',
      },
    );

    observer.observe(sentinelRef.current);

    return () => {
      observer.disconnect();
    };
  }, [enableScrollSentinel, onScrollSentinelChange]);

  // Dynamic top padding using CSS variables that account for safe area insets.
  // On PWA/web: env(safe-area-inset-top) = 0, so this equals the fixed header height.
  // On native Capacitor: adds the device's safe area inset (e.g., ~50px on notched iPhones).
  const headerPaddingStyle: React.CSSProperties = hasHeader
    ? { paddingTop: 'var(--header-height)' }
    : {};

  // AIDEV-NOTE: Gradient configuration mapping - reusable dissolution system
  const gradientConfig = {
    none: { height: 'h-0', gradient: '' },
    subtle: { height: 'h-8', gradient: 'bg-fade-header-subtle' },
    standard: { height: 'h-16', gradient: 'bg-fade-header-standard' },
    prominent: { height: 'h-24', gradient: 'bg-fade-header-prominent' },
  };

  const selectedConfig = gradientConfig[gradientType];
  const finalGradientHeight = gradientHeight || selectedConfig.height;
  const finalGradientClass = gradientClassName || selectedConfig.gradient;

  // AIDEV-NOTE: Routes handle their own bottom padding to compensate for footer height
  // No bottom padding at Layout level to avoid double-padding issues

  return (
    <div className="bg-mb-bg flex flex-row justify-center w-full flex-1 min-h-0">
      <div className="bg-mb-bg w-full relative flex flex-col flex-1 min-h-0">
        {/* Header Slot - Custom header from parent layout */}
        {hasHeader && (
          <div className="fixed top-0 app-fixed z-30 bg-mb-bg pt-safe">{customHeader}</div>
        )}

        {/* Main Content - Flex child that allows routes to manage their own scroll */}
        <main
          className={`relative flex-1 min-h-0 ${mainClassName || ''}`}
          style={{ ...headerPaddingStyle, ...mainStyle }}
        >
          {/* AIDEV-NOTE: Scroll sentinel for IntersectionObserver - invisible marker at content top */}
          {enableScrollSentinel && (
            <div
              ref={sentinelRef}
              className="absolute left-0 w-full h-px pointer-events-none"
              style={{
                top: 'var(--header-height)',
              }}
              aria-hidden="true"
            />
          )}

          {/* AIDEV-NOTE: Reusable gradient overlay for header dissolution - configurable via props */}
          {hasHeader && gradientType !== 'none' && (
            <div
              className={`fixed app-fixed ${finalGradientHeight} ${finalGradientClass} pointer-events-none z-20`}
              style={{
                top: 'var(--header-height)',
              }}
            />
          )}
          {children}
        </main>

        {/* Footer Slot - Custom footer from parent layout */}
        {/* AIDEV-NOTE: Bottom nav offset accounts for safe area (home indicator on iOS).
         * On web: --bottom-nav-offset = 12px. On native: 12px + env(safe-area-inset-bottom). */}
        {hasFooter && (
          <div
            className="fixed app-fixed z-10"
            style={footerStyle ?? { bottom: 'var(--bottom-nav-offset)' }}
          >
            {customFooter}
          </div>
        )}
      </div>
    </div>
  );
};
